import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useOrgData } from "./OrgDataContext";
import { useSocket } from "./SocketContext";
import { extractAndFilterSubtypes, sortLogsByTime } from "../services/log/log-service";
import { useGetData } from "../services/api/api-tools";
import { detectPatternsInLogs } from "../services/entity/pattern/pattern-service";
import { getLogs, JOURNAL_DISPLAY_OPTIONS, PRODUCTION_MODE_CUSTOMER } from "../services/journal/journal-service";
import { DefaultLog } from "../pages/journal/components/main-panel/DefaultLog";
import { DefaultPatternLog } from "../pages/journal/components/main-panel/DefaultPatternLog";
import { LISIPatternLog } from "../pages/journal/components/main-panel/customer-logs/lisi/LISIPatternLog";
import { LISILog } from "../pages/journal/components/main-panel/customer-logs/lisi/LISILog";
import { detectLISIPatternsInLogs } from "../services/entity/pattern/customer/lisi-pattern-service";

export const DEFAULT_HOURS_TO_LOOK_BACK = 24;
export const VISIBLE_LOGS_IN_PANEL_COUNT = 25;

const JournalContext = createContext();

export const JournalProvider = ({ children }) => {
  const { workstationSelected, eventTypes, isWorkstationDataLoading } = useOrgData();
  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();

  // State flags
  const [rawLogs, setRawLogs] = useState([]);
  const [isLogsProcessing, setIsLogsProcessing] = useState(true);
  const [isInitialRequestDone, setIsInitialRequestDone] = useState(false);
  const isClientSetupDateExceeded = useRef(false);
  const lastLogRetrievalDate = useRef(Date.now()); // Keep the reference to the last log date retrieved
  const [focusedLogDate, setFocusedLogDate] = useState(null);

  const allowFetchingMoreLogs = !isWorkstationDataLoading && isInitialRequestDone && !isClientSetupDateExceeded.current;

  // Filters state
  const [selectedDisplayOption, setSelectedDisplayOption] = useState(JOURNAL_DISPLAY_OPTIONS[0]);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: Date.now() - 24 * 60 * 60 * 1000,
    stop: Date.now(),
  });

  // Retrieve the available patterns for the selected machine
  const { data: allPatterns, refetch: refetchPatternsDetection } = useGetData('patterns', 'patterns');
  const patterns = allPatterns?.filter((pattern) => pattern?.workstation?.id === workstationSelected.id) || [];
  const { data: patternExecutions } = useGetData('pattern_executions', `pattern_executions`);

  const getLogsByDateRange = async (startDateTimestamp, stopDateTimestamp, minLogsToLoad=0, isInitialRequest=false) => {
    if (isWorkstationDataLoading) return;

    try {
      // Get event type subtypes linked to the selected workstation
      const subtypes = extractAndFilterSubtypes(eventTypes, workstationSelected);
      if (subtypes.length === 0) {
        setRawLogs([]);
        return;
      }

      setIsLogsProcessing(true);

      const installationClientDate = parseInt(process.env.REACT_APP_INSTALLATION_CLIENT_DATE, 10) * 1000;
      let loadedLogs = []; // Local accumulator for THIS call only
      let loopIndex = 0;

      while (loadedLogs.length < minLogsToLoad || loopIndex === 0) {
        const startTs = loopIndex === 0 ? startDateTimestamp : startDateTimestamp - DEFAULT_HOURS_TO_LOOK_BACK * (2 ** loopIndex - 1) * 60 * 60 * 1000;
        const stopTs = loopIndex === 0 ? stopDateTimestamp : startDateTimestamp - DEFAULT_HOURS_TO_LOOK_BACK * (2 ** (loopIndex - 1) - 1) * 60 * 60 * 1000;

        if (startTs < installationClientDate) {
          isClientSetupDateExceeded.current = true;
          break;
        }

        const logs = await getLogs(subtypes, startTs, stopTs);
        lastLogRetrievalDate.current = startTs;

        loadedLogs.push(...logs);

        loopIndex += 1;
      }

      if (isInitialRequest) setRawLogs(loadedLogs);
      else setRawLogs((prevLogs) => [...prevLogs, ...loadedLogs]);

    } catch (error) {
      console.error('Error fetching logs:', error);
    }
    finally {
      setIsLogsProcessing(false);
      if (!isInitialRequestDone) setIsInitialRequestDone(true);
    }
  };


  // Retrieve the raw data when a machine or a date is selected / modified
  useEffect(() => {
    if (!selectedDateRange?.start || !selectedDateRange?.stop || !eventTypes?.length) return;

    isClientSetupDateExceeded.current = false;
    lastLogRetrievalDate.current = selectedDateRange.start;

    getLogsByDateRange(selectedDateRange.start, selectedDateRange.stop, VISIBLE_LOGS_IN_PANEL_COUNT, true);
  }, [selectedDateRange, eventTypes, workstationSelected, isWorkstationDataLoading]);

  // Allow the user to retrieve more logs (e.g. by scrolling)
  const getMoreLogs = () => {
    getLogsByDateRange(lastLogRetrievalDate.current - 24 * 60 * 60 * 1000, lastLogRetrievalDate.current, 1);
  };

  // Process the raw data by applying various operations and add high-level logs (specific logs, patterns)
  const logs = useMemo(() => {
    if (!rawLogs || !patterns || !patternExecutions) return [];

    /* Select the component to render depending on the customer if needed */
    let [LogComponent, PatternLogComponent, detectPatternsFunc] = [DefaultLog, DefaultPatternLog, detectPatternsInLogs];

    if (parseInt(process.env.REACT_APP_PRODUCTION_MODE) === PRODUCTION_MODE_CUSTOMER) {
      [LogComponent, PatternLogComponent, detectPatternsFunc] = {
        'LISI': [LISILog, LISIPatternLog, detectLISIPatternsInLogs],
      }[process.env.REACT_APP_CUSTOMER_NAME];
    }

    // Logs component
    const rawLogsComponents = rawLogs.map((log) => ({ ...log, render: LogComponent }));

    // Patterns detection & component
    const detectedPatternsComponents = detectPatternsFunc(rawLogs, patterns, patternExecutions)
      .filter((pattern) => new Date(pattern.time).getTime() >= lastLogRetrievalDate.current)
      .map((pattern) => ({ ...pattern, render: PatternLogComponent }));

    const combinedLogs = [...rawLogsComponents, ...detectedPatternsComponents].flat();
    return sortLogsByTime(combinedLogs);
  }, [rawLogs, patterns, patternExecutions]);

  // TODO Manage new logs received
  // Handle the reception of socket events (like receiving live logs)
  // useEffect(() => {
  //   if (!socket || workstationSelected?.id === 0) return;
  //
  //   const handleEvent = (data) => {
  //     console.info('[DEBUG] New event received:', data);
  //     data.time = moment.unix(data.time).toISOString();
  //     const subtypes = extractAndFilterSubtypes(eventTypes, workstationSelected);
  //     const isOperatorLog = data.log_id;
  //     if (subtypes.some((subtype) => parseInt(subtype) === parseInt(data.subtype))) {
  //       if (!isOperatorLog) {
  //         // setIsLogsProcessing(true);
  //         setRawLogs((allLogs) => {
  //           return [...allLogs, data];
  //         });
  //       }
  //     }
  //   };
  //
  //   console.info('[DEBUG] Subscribe to events');
  //   subscribeToEvent('event', handleEvent);
  //
  //   return () => {
  //     unsubscribeFromEvent('event', handleEvent);
  //   };
  // }, [socket, workstationSelected, eventTypes]);

  const contextValue = useMemo(
    () => ({
      logs,
      selectedDisplayOption,
      setSelectedDisplayOption,
      isLogsProcessing,
      setIsLogsProcessing,
      selectedDateRange,
      setSelectedDateRange,
      allowFetchingMoreLogs,
      refetchPatternsDetection,
      lastLogRetrievalDate,
      getMoreLogs,
      focusedLogDate,
      setFocusedLogDate,
      patternExecutions,
    }),
    [
      logs,
      selectedDisplayOption,
      setSelectedDisplayOption,
      isLogsProcessing,
      setIsLogsProcessing,
      selectedDateRange,
      setSelectedDateRange,
      allowFetchingMoreLogs,
      refetchPatternsDetection,
      lastLogRetrievalDate,
      getMoreLogs,
      focusedLogDate,
      setFocusedLogDate,
      patternExecutions,
    ],
  );

  return <JournalContext.Provider value={contextValue}>{children}</JournalContext.Provider>;
};

export const useJournal = () => {
  return useContext(JournalContext);
};
